/**
 * DomainSmtpTab — 收件域右侧"SMTP/收信设置"Tab
 * ① 该域名的默认转发邮箱（按域名，单服务多域名各自独立）。
 * ② 服务器全局设置：收件端口 / 收发开关 / DNS 上线配置（所有收件域共用）。
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Field,
  InfoLabel,
  Input,
  Switch,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Divider,
} from '@fluentui/react-components'
import { SaveRegular, AddRegular, DeleteRegular, GlobeRegular } from '@fluentui/react-icons'
import { domainsApi } from '@/api/domains'
import { serverConfigApi, type ServerConfigInput } from '@/api/serverConfig'
import type { Domain, MxRecord, ServerConfig } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '16px',
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  mxRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  mxRaw: { display: 'flex', flexDirection: 'column', gap: '8px' },
})

function updateMx(list: MxRecord[] | undefined, idx: number, patch: Partial<MxRecord>): MxRecord[] {
  const base = list ?? []
  return base.map((m, i) => (i === idx ? { ...m, ...patch } : m))
}

export default function DomainSmtpTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()

  // ── ① 该域名的默认转发邮箱 ──
  const [fwd, setFwd] = useState<string | null>(domain.defaultForwardEmail ?? '')
  const [fwdDirty, setFwdDirty] = useState(false)

  const saveFwdMutation = useMutation({
    mutationFn: async () => {
      // 后端 update_domain 为整行覆盖更新，需携带全部字段
      await domainsApi.update(domain.id, {
        name: domain.name,
        description: domain.description,
        max_quota_bytes: domain.max_quota_bytes,
        max_users: domain.max_users,
        default_forward_email: (fwd ?? '').trim() || null,
      })
      return domain.id
    },
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: ['domains'] })
      await queryClient.invalidateQueries({ queryKey: ['domain', id] })
      setFwdDirty(false)
      toast.success('已保存', `${domain.name} 的默认转发邮箱已更新，实时生效`)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  // ── ② 服务器全局设置 ──
  const { data: config, isLoading } = useQuery({
    queryKey: ['server-config'],
    queryFn: () => serverConfigApi.get(),
  })
  const [form, setForm] = useState<ServerConfig | null>(null)
  const activeForm = form ?? config

  const saveGlobalMutation = useMutation({
    mutationFn: async (input: ServerConfigInput) => serverConfigApi.put(input),
    onSuccess: () => {
      setForm(null)
      toast.success('已保存', '服务器全局配置已下发')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  const set = (patch: Partial<ServerConfig>) => {
    const base = form ?? config
    if (!base) return
    setForm({ ...base, ...patch })
  }

  const handleSaveGlobal = () => {
    const port = Number(activeForm?.smtp_port)
    if (!port || port < 1 || port > 65535) {
      toast.error('请填写有效的端口号（1-65535）')
      return
    }
    saveGlobalMutation.mutate({
      smtp_port: port,
      default_forward_email: null,
      allow_inbound: activeForm?.allow_inbound ?? true,
      allow_outbound: activeForm?.allow_outbound ?? true,
      mx_records: (activeForm?.mx_records ?? [])
        .filter((m) => m.host.trim() !== '')
        .map((m) => ({ priority: m.priority, host: m.host.trim() })),
      server_ip: activeForm?.server_ip?.trim() || '',
    })
  }

  const globalDirty = JSON.stringify(form) !== 'null'

  return (
    <div className={styles.root}>
      {/* ① 按域名的默认转发邮箱 */}
      <Card size="small">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <GlobeRegular />
          <Text style={{ fontWeight: 600 }}>默认转发邮箱（{domain.name}）</Text>
        </div>
        <MessageBar intent="info" style={{ marginTop: 10, marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>按域名生效</MessageBarTitle>
            该域名下邮件未匹配到本地邮箱 / 别名时，收件将被转发到此处填写的地址；留空表示不转发。各收件域可独立配置，实时生效。
          </MessageBarBody>
        </MessageBar>
        <Field
          label={<InfoLabel info="未匹配到本地邮箱/别名时收件去向；留空表示不转发。">默认转发邮箱</InfoLabel>}
          hint="可选，例如 postmaster@example.com"
        >
          <Input
            value={fwd ?? ''}
            onChange={(_, d) => {
              setFwd(d.value)
              setFwdDirty(true)
            }}
            placeholder="postmaster@example.com"
          />
        </Field>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            disabled={!fwdDirty || saveFwdMutation.isPending}
            onClick={() => saveFwdMutation.mutate()}
          >
            {saveFwdMutation.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </Card>

      <Divider />

      {/* ② 服务器全局设置 */}
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>服务器全局</MessageBarTitle>
          以下设置作用于整个服务器（所有收件域共用），与上方"按域名"的默认转发维度不同。
        </MessageBarBody>
      </MessageBar>

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>收件与发信</Text>
        <div className={styles.form} style={{ marginTop: 12 }}>
          {isLoading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}><Spinner size="small" label="加载中..." /></div>
          ) : (
            <>
              <Field label={<InfoLabel info="服务器接收外部来信的 SMTP 端口。">收件端口</InfoLabel>} hint="默认 25">
                <Input
                  type="number"
                  value={String(activeForm?.smtp_port ?? '')}
                  onChange={(_, d) => set({ smtp_port: Number(d.value) })}
                  style={{ maxWidth: '200px' }}
                />
              </Field>
              <Field label={<InfoLabel info="是否接收外部来信。关闭后服务器拒绝入站 SMTP。">允许收信</InfoLabel>}>
                <Switch
                  checked={activeForm?.allow_inbound ?? true}
                  onChange={(_, d) => set({ allow_inbound: d.checked })}
                  label={(activeForm?.allow_inbound ?? true) ? '允许收信' : '禁止收信'}
                />
              </Field>
              <Field label={<InfoLabel info="是否允许对外发信。关闭后出站邮件将被拒绝。">允许发信</InfoLabel>}>
                <Switch
                  checked={activeForm?.allow_outbound ?? true}
                  onChange={(_, d) => set({ allow_outbound: d.checked })}
                  label={(activeForm?.allow_outbound ?? true) ? '允许发信' : '禁止发信'}
                />
              </Field>
            </>
          )}
        </div>
      </Card>

      <Divider style={{ marginTop: '4px' }} />

      {/* DNS 上线配置（全局） */}
      <Text style={{ fontSize: '15px', fontWeight: 600, color: tokens.colorNeutralForeground1 }}>
        DNS 上线配置
      </Text>
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>仅供 DNS 上线展示</MessageBarTitle>
          以下两项不参与邮件收发逻辑，仅用于在「上线配置」页生成 A / MX 记录，供你复制到域名服务商完成解析。
        </MessageBarBody>
      </MessageBar>

      <Card size="small">
        <div className={styles.form}>
          <Field label={<InfoLabel info="收件域 A 记录指向的本服务器公网 IP，供上线指引展示与一键填充 MX 主机。">服务器 IP（A 记录）</InfoLabel>} hint="例如 203.0.113.10">
            <Input
              value={activeForm?.server_ip ?? ''}
              onChange={(_, d) => set({ server_ip: d.value })}
              placeholder="203.0.113.10"
              style={{ maxWidth: '240px' }}
            />
          </Field>
          <Field label={<InfoLabel info="收信域的 MX 服务器列表，支持多值高可用。每条独立生成 DNS 记录，越小优先级越高。">MX 记录</InfoLabel>} hint="至少一条；多台服务器时不同优先级分开配置">
            <div className={styles.mxRaw}>
              {(activeForm?.mx_records ?? []).map((mx, idx) => (
                <div key={idx} className={styles.mxRow}>
                  <Input
                    type="number"
                    value={String(mx.priority)}
                    onChange={(_, d) => set({ mx_records: updateMx(activeForm?.mx_records, idx, { priority: Number(d.value) }) })}
                    placeholder="优先级"
                    style={{ maxWidth: '96px' }}
                  />
                  <Input
                    value={mx.host}
                    onChange={(_, d) => set({ mx_records: updateMx(activeForm?.mx_records, idx, { host: d.value }) })}
                    placeholder="mail.example.com"
                    style={{ flex: 1 }}
                  />
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    aria-label="删除该 MX 记录"
                    onClick={() => set({ mx_records: (activeForm?.mx_records ?? []).filter((_, i) => i !== idx) })}
                  />
                </div>
              ))}
              <div>
                <Button
                  size="small"
                  icon={<AddRegular />}
                  onClick={() => set({ mx_records: [...(activeForm?.mx_records ?? []), { priority: 10, host: '' }] })}
                >
                  添加 MX 记录
                </Button>
              </div>
            </div>
          </Field>
        </div>
      </Card>

      <div className={styles.hint}>全局配置更新时间：{activeForm?.updated_at ?? '尚未保存过'}</div>

      <div className={styles.actions}>
        {globalDirty && (
          <Button appearance="subtle" onClick={() => setForm(null)}>
            撤销
          </Button>
        )}
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          onClick={handleSaveGlobal}
          disabled={saveGlobalMutation.isPending}
        >
          {saveGlobalMutation.isPending ? '保存中…' : '保存全局配置'}
        </Button>
      </div>
    </div>
  )
}